import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { loadForms, setCurrentForm } from '../../store/participantService';
import { loadUser } from '../../store/userService';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../utils/formValidation/commonConstants';
import { stableSort } from '../../utils/sortFunctions';
import { hasRole, SUPERUSER } from '../../utils/userRoles';
import Button from '../formElements/Button';
import DynamicSearch from '../search/DynamicSearch';
import ResultsTable from '../table/ResultsTable';

import FormTableRow from './FormTableRow';

import app from '../../app.module.css';
import form from './formStyles/formToolbar.module.css';

const headCells = [
    { id: 'id', numeric: false, label: 'ID' },
    { id: 'name', numeric: false, label: 'Title' },
    { id: 'dateCreated', numeric: false, label: 'Date Created' },
    { id: 'inactive', numeric: false, label: 'Active' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'dateCreated',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const adminRoles = [SUPERUSER];

const FormManagement = () => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState(rows);
    const [searchTerm, setSearchTerm] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [selectedRowId, setSelectedRowId] = useState('');

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const currentForm = useSelector((state) => state.entities.participantService.currentForm);
    const forms = useSelector((state) => state.entities.participantService.forms);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadForms());
        dispatch(loadUser(loggedInUser.id));
    }, []);

    useEffect(() => {
        setRows(forms);
        setFilteredRows(forms);
    }, [forms]);

    useEffect(() => {
        if (selectedRowId && currentForm.id === selectedRowId) {
            navigate('/build_form');
            setSelectedRowId('');
        }
    }, [currentForm, selectedRowId]);

    useEffect(() => {
        const filteredRows = !searchTerm
            ? rows
            : rows.filter((el) => el.name.toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredRows(filteredRows);
    }, [searchTerm]);

    useEffect(() => {
        if (successMessage === `Form has been saved successfully`) dispatch(loadForms());
    }, [successMessage]);

    // HELPER FNS
    const createRows = () => {
        return stableSort(filteredRows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <FormTableRow key={el.id} row={el} onEdit={onEdit} />
        ));
    };

    // EVENT HANDLERS
    const onSearch = (searchRes) => setSearchTerm(searchRes);

    const onCreateForm = () => {
        dispatch(setCurrentForm({}));
        navigate('/build_form');
    };

    const onEdit = (id) => setSelectedRowId(id);

    // RENDER
    return (
        <div className={app.pageContainer}>
            <h1 className={app.mainHeading}>Manage Form</h1>
            <div className={form.formToolBar}>
                <DynamicSearch
                    search={onSearch}
                    searchTerm={searchTerm}
                    placeholder="Enter search term"
                />
            </div>
            {filteredRows.length < 1 ? (
                <div>No forms found</div>
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    sortOrder={rowMetaData.order}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={rows.length}
                />
            )}
            <Button
                id="CreateFormButton"
                disabled={!hasRole(adminRoles, roles)}
                type="button"
                content="Create Form"
                icon={true}
                onClick={onCreateForm}
            />
        </div>
    );
};

export default FormManagement;
